'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var mplTokenMetadata = require('@metaplex-foundation/mpl-token-metadata');
var web3_js = require('@solana/web3.js');
var Operation = require('../../../types/Operation.cjs');
var TransactionBuilder = require('../../../utils/TransactionBuilder.cjs');

// -----------------
// Operation
// -----------------

const Key = 'VerifyNftCollectionOperation';

/**
 * Verifies the collection of an NFT or SFT.
 *
 * ```ts
 * await metaplex
 *   .nfts()
 *   .verifyCollection({ mintAddress, collectionMintAddress };
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const verifyNftCollectionOperation = Operation.useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const verifyNftCollectionOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    return verifyNftCollectionBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Verifies the collection of an NFT or SFT.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .verifyCollection({ mintAddress, collectionMintAddress });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const verifyNftCollectionBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    mintAddress,
    collectionMintAddress,
    isSizedCollection = true,
    isDelegated = false,
    collectionAuthority = metaplex.identity(),
    collectionUpdateAuthority = metaplex.identity().publicKey
  } = params;

  // Programs.
  const systemProgram = metaplex.programs().getSystem(programs);
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);

  // Accounts.
  const metadata = metaplex.nfts().pdas().metadata({
    mint: mintAddress,
    programs
  });
  const collectionMetadata = metaplex.nfts().pdas().metadata({
    mint: collectionMintAddress,
    programs
  });
  const collectionEdition = metaplex.nfts().pdas().masterEdition({
    mint: collectionMintAddress,
    programs
  });
  if (isDelegated === 'legacyDelegate' || isDelegated === true) {
    const accounts = {
      metadata,
      collectionAuthority: collectionAuthority.publicKey,
      payer: payer.publicKey,
      collectionMint: collectionMintAddress,
      collection: collectionMetadata,
      collectionMasterEditionAccount: collectionEdition
    };
    const instruction = isSizedCollection ? mplTokenMetadata.createVerifySizedCollectionItemInstruction(accounts, tokenMetadataProgram.address) : mplTokenMetadata.createVerifyCollectionInstruction(accounts, tokenMetadataProgram.address);
    instruction.keys.push({
      pubkey: metaplex.nfts().pdas().collectionAuthorityRecord({
        mint: collectionMintAddress,
        collectionAuthority: collectionAuthority.publicKey,
        programs
      }),
      isWritable: false,
      isSigner: false
    });
    return TransactionBuilder.TransactionBuilder.make().setFeePayer(payer).add({
      instruction,
      signers: [payer, collectionAuthority],
      key: params.instructionKey ?? 'verifyCollection'
    });
  }
  const delegateRecord = isDelegated === 'metadataDelegate' ? metaplex.nfts().pdas().metadataDelegateRecord({
    mint: collectionMintAddress,
    type: 'CollectionV1',
    updateAuthority: collectionUpdateAuthority,
    delegate: collectionAuthority.publicKey,
    programs
  }) : undefined;
  return TransactionBuilder.TransactionBuilder.make().setFeePayer(payer).add({
    instruction: mplTokenMetadata.createVerifyInstruction({
      authority: collectionAuthority.publicKey,
      delegateRecord,
      metadata,
      collectionMint: collectionMintAddress,
      collectionMetadata,
      collectionMasterEdition: collectionEdition,
      systemProgram: systemProgram.address,
      sysvarInstructions: web3_js.SYSVAR_INSTRUCTIONS_PUBKEY
    }, {
      verificationArgs: mplTokenMetadata.VerificationArgs.CollectionV1
    }, tokenMetadataProgram.address),
    signers: [collectionAuthority],
    key: params.instructionKey ?? 'verifyCollection'
  });
};

exports.verifyNftCollectionBuilder = verifyNftCollectionBuilder;
exports.verifyNftCollectionOperation = verifyNftCollectionOperation;
exports.verifyNftCollectionOperationHandler = verifyNftCollectionOperationHandler;
//# sourceMappingURL=verifyNftCollection.cjs.map
